import React, { useCallback, useEffect, useState } from 'react';

import {
	ApButtonLink,
	ApCheckbox,
	ApIcon,
	colors,
} from '@alixpartners/ui-components';
import { Tooltip } from 'react-tippy';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { HeaderDropdown } from 'components/HeaderDropdown';
import {
	deleteTransactions,
	restoreTransactions,
	getTransactionsBin,
	getTransactionsBinDimensions,
} from 'utils/apiRequests';
import { dateFormatter, colFormatters, getCols } from './utils';
import { getHierarchy } from 'Investigate/SearchDialog/utils';

import { getCurrencySymbolFromOptions } from 'Details/utils';
import {
	ActionText,
	Header,
	HeaderActions,
	Wrapper,
	Content,
	SelectDimension,
	TableHeader,
	TableHeaderCol,
	TableRow,
	TableCell,
	RestoreButton,
	DeleteButton,
	Pagination,
	PaginationText,
	SelectText,
	EllipsisSpan,
	TooltipRow,
	TooltipContent,
} from './Bin.styles';
import { DeleteForeverModal } from './DeleteForeverModal';
import { RestoreModal } from './RestoreModal';
import isEqual from 'lodash/isEqual';

const BinView = ({ currencySymbol }) => {
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(null);
	const [selectedDimension, setSelectedDimension] = useState(null);
	const [transactions, setTransactions] = useState(null);
	const [selectedTransactions, setSelectedTransactions] = useState([]);
	const [isAllSelectedTransactions, setIsAllSelectedTranscations] = useState(
		false
	);
	const [dimensions, setDimensions] = useState([]);
	const [sorting, setSorting] = useState({
		columnIndex: 1,
		direction: 'ASC',
	});
	const [itemsOnPage, addItemsOnPage] = useState(25);

	const getData = useCallback(
		() => {
			if (selectedDimension && dimensions.length) {
				const dimensionParam = Array(selectedDimension.level).fill(
					selectedDimension.dimensionName
				);
				const params = {
					dimensions: dimensionParam,
					sortIndex: sorting.columnIndex,

					sortDirection: sorting.direction,
					skip: 0,
					take: itemsOnPage,
					filters: selectedDimension.filter ? [selectedDimension.filter] : [],
					filterValues: selectedDimension.filterValue
						? [selectedDimension.filterValue]
						: [],
				};
				getTransactionsBin(params).then(result => {
					setTransactions(result);
				});
			}
		},
		[
			dimensions.length,
			itemsOnPage,
			selectedDimension,
			sorting.columnIndex,
			sorting.direction,
		]
	);

	const getActionParams = useCallback(
		() =>
			selectedTransactions.map(transaction => {
				return {
					dimensionName: selectedDimension.dimensionName,
					value: transaction.value,
					dimensionValues: transaction.dimensionValues,
				};
			}),
		[selectedDimension, selectedTransactions]
	);

	useEffect(() => {
		getTransactionsBinDimensions().then(result => {
			setDimensions(result);
		});
	}, []);

	useEffect(
		() => {
			if (!selectedDimension && dimensions.length) {
				setSelectedDimension({
					...dimensions[0],
					label: dimensions[0].displayName.replaceAll('_', ' '),
					value: dimensions[0].displayName,
				});
			}
			getData();
		},
		[dimensions, getData, selectedDimension]
	);

	const confirmDelete = useCallback(
		() => {
			deleteTransactions(getActionParams()).then(() => {
				setIsDeleteDialogOpen(false);

				getData();

				setSelectedTransactions([]);
				setIsAllSelectedTranscations(false);
			});
		},
		[getActionParams, getData]
	);

	const confirmRestore = useCallback(
		() => {
			restoreTransactions(getActionParams()).then(() => {
				setIsRestoreDialogOpen(false);

				getData();

				setSelectedTransactions([]);
				setIsAllSelectedTranscations(false);
			});
		},
		[getActionParams, getData]
	);

	const columns = getCols(transactions, selectedDimension);
	const selectedTranscationsAmount = selectedTransactions.length
		? selectedTransactions.reduce((acc, transaction) => {
				acc += transaction.transactionsCount;

				return acc;
		  }, 0)
		: 0;

	const handleTransactionsChange = transaction => {
		let updatedItems = [];
		const currentIndex = selectedTransactions.findIndex(item =>
			isEqual(transaction.dimensionValues, item.dimensionValues)
		);

		if (currentIndex === -1) {
			updatedItems = [...selectedTransactions, transaction];

			if (updatedItems.length === transactions.totalCount) {
				setIsAllSelectedTranscations(true);
			}
		} else {
			updatedItems = [
				...selectedTransactions.slice(0, currentIndex),
				...selectedTransactions.slice(currentIndex + 1),
			];
			setIsAllSelectedTranscations(false);
		}

		setSelectedTransactions(updatedItems);
	};

	const isTransactionSelected = row => {
		return !!selectedTransactions.find(item =>
			isEqual(row.dimensionValues, item.dimensionValues)
		);
	};

	return (
		<Wrapper>
			<Header>
				<SelectDimension>
					<SelectText>Show by:</SelectText>
					{selectedDimension && (
						<HeaderDropdown
							options={dimensions.map(dimension => ({
								...dimension,
								label: dimension.displayName.replaceAll('_', ' '),
								value: dimension.displayName,
							}))}
							selected={selectedDimension}
							onChange={value => {
								setSelectedDimension(value);
							}}
						/>
					)}
				</SelectDimension>

				<HeaderActions>
					<ActionText>Select item(s) to:</ActionText>
					<DeleteButton
						disabled={selectedTransactions.length === 0}
						onClick={() => setIsDeleteDialogOpen(true)}
					>
						delete
					</DeleteButton>
					<RestoreButton
						disabled={selectedTransactions.length === 0}
						iconName="baseline_history"
						onClick={() => setIsRestoreDialogOpen(true)}
					>
						restore
					</RestoreButton>
				</HeaderActions>
			</Header>
			{transactions && transactions.totalCount ? (
				<Content>
					<TableHeader>
						<TableHeaderCol width={64}>
							<ApCheckbox
								onChange={() => {
									if (isAllSelectedTransactions) {
										setSelectedTransactions([]);
									} else {
										setSelectedTransactions(transactions.body);
									}
									setIsAllSelectedTranscations(!isAllSelectedTransactions);
								}}
								checked={isAllSelectedTransactions}
							/>
						</TableHeaderCol>
						{columns.map(column => (
							<TableHeaderCol
								key={`header-${column.colName}`}
								align={column.align}
								width={column.width}
								onClick={() =>
									setSorting({
										columnIndex: column.sortIndex,
										direction: sorting.direction === 'ASC' ? 'DESC' : 'ASC',
									})
								}
							>
								<span>{column.title}</span>
								{column.sortIndex === sorting.columnIndex && (
									<ApIcon
										iconName={
											sorting.direction === 'ASC'
												? 'arrow_upward'
												: 'arrow_downward'
										}
										iconColor={colors.grey4}
										iconSize={16}
									/>
								)}
							</TableHeaderCol>
						))}
					</TableHeader>
					{transactions.body.map((row, index) => (
						<TableRow
							key={`row-${row.value}-${index}`}
							isSelected={isTransactionSelected(row)}
						>
							<TableCell width={64}>
								<ApCheckbox
									onChange={() => handleTransactionsChange(row)}
									checked={isTransactionSelected(row)}
								/>
							</TableCell>
							{columns.map(column => (
								<TableCell
									key={`cell-${column.colName}`}
									align={column.align}
									width={column.width}
								>
									{column.colName === 'period' ? (
										`${dateFormatter(row.minDate)}–${dateFormatter(
											row.maxDate
										)}`
									) : column.colName === 'value' ? (
										!!getHierarchy(row) ? (
											<Tooltip
												position="top"
												animation="fade"
												arrow
												html={
													<TooltipContent>{getHierarchy(row)}</TooltipContent>
												}
												style={{ width: '100%' }}
											>
												<TooltipRow>
													<EllipsisSpan>{row[column.colName]}</EllipsisSpan>
												</TooltipRow>
											</Tooltip>
										) : (
											<TooltipRow>
												<EllipsisSpan>{row[column.colName]}</EllipsisSpan>
											</TooltipRow>
										)
									) : colFormatters[column.colName] ? (
										colFormatters[column.colName](
											row[column.colName],
											currencySymbol
										)
									) : (
										row[column.colName]
									)}
								</TableCell>
							))}
						</TableRow>
					))}
					{transactions.totalCount > 25 && (
						<Pagination>
							<PaginationText>
								{itemsOnPage}/{transactions.totalCount}
							</PaginationText>
							{itemsOnPage < transactions.totalCount && (
								<ApButtonLink
									onClick={() => {
										addItemsOnPage(
											Math.min(itemsOnPage + 10, transactions.totalCount)
										);
									}}
								>
									show more
								</ApButtonLink>
							)}
						</Pagination>
					)}
				</Content>
			) : null}
			{isDeleteDialogOpen && (
				<DeleteForeverModal
					confirmDelete={confirmDelete}
					onClose={() => setIsDeleteDialogOpen(false)}
					transactionsAmount={selectedTranscationsAmount}
				/>
			)}

			{isRestoreDialogOpen && (
				<RestoreModal
					confirmRestore={confirmRestore}
					onClose={() => setIsRestoreDialogOpen(false)}
					transactionsAmount={selectedTranscationsAmount}
				/>
			)}
		</Wrapper>
	);
};

export const Bin = props => (
	<QueryStringContext.Consumer>
		{({ currency }) => (
			<SettingsOptionsContext.Consumer>
				{({ options }) => {
					const { currency: currencyList } = options;
					const currencySymbol = getCurrencySymbolFromOptions(
						currencyList,
						currency
					);
					return <BinView {...props} currencySymbol={currencySymbol} />;
				}}
			</SettingsOptionsContext.Consumer>
		)}
	</QueryStringContext.Consumer>
);

export default Bin;
